import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './CookieConsent.module.css';

// Cookie consent storage key
const CONSENT_KEY = 'integrova_cookie_consent';

interface ConsentState {
    necessary: boolean;
    analytics: boolean;
    timestamp: number;
}

export const CookieConsent: React.FC = () => {
    const { language } = useLanguage();
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [consent, setConsent] = useState<ConsentState>({
        necessary: true, // Always required
        analytics: false,
        timestamp: 0
    });

    const content = {
        de: {
            title: 'Cookie-Einstellungen',
            description: 'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Einige Cookies sind technisch notwendig, andere helfen uns, unsere Website zu verbessern.',
            moreInfo: 'Weitere Informationen finden Sie in unserer',
            privacyLink: 'Datenschutzerklärung',
            acceptAll: 'Alle akzeptieren',
            acceptNecessary: 'Nur notwendige',
            saveSettings: 'Einstellungen speichern',
            settings: 'Einstellungen',
            categories: {
                necessary: {
                    title: 'Notwendige Cookies',
                    description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.',
                    required: '(erforderlich)'
                },
                analytics: {
                    title: 'Analyse-Cookies',
                    description: 'Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen.'
                }
            }
        },
        en: {
            title: 'Cookie Settings',
            description: 'We use cookies to provide you with the best possible experience on our website. Some cookies are technically necessary, others help us improve our website.',
            moreInfo: 'For more information, please see our',
            privacyLink: 'Privacy Policy',
            acceptAll: 'Accept All',
            acceptNecessary: 'Necessary Only',
            saveSettings: 'Save Settings',
            settings: 'Settings',
            categories: {
                necessary: {
                    title: 'Necessary Cookies',
                    description: 'These cookies are required for the basic functions of the website.',
                    required: '(required)'
                },
                analytics: {
                    title: 'Analytics Cookies',
                    description: 'These cookies help us understand how visitors use our website.'
                }
            }
        }
    };

    const t = content[language];

    // Check for existing consent on mount
    useEffect(() => {
        const storedConsent = localStorage.getItem(CONSENT_KEY);
        if (storedConsent) {
            try {
                const parsed = JSON.parse(storedConsent);
                setConsent(parsed);
                setIsVisible(false);
            } catch {
                setIsVisible(true);
            }
        } else {
            // Delay showing the banner slightly for better UX
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const saveConsent = (consentState: ConsentState) => {
        const newConsent = { ...consentState, timestamp: Date.now() };
        localStorage.setItem(CONSENT_KEY, JSON.stringify(newConsent));
        setConsent(newConsent);
        setIsVisible(false);
        setShowSettings(false);

        // Trigger analytics if accepted (placeholder for future implementation)
        if (consentState.analytics) {
            console.log('Analytics consent granted - ready for Google Analytics');
            // window.gtag && window.gtag('consent', 'update', { analytics_storage: 'granted' });
        }
    };

    const handleAcceptAll = () => {
        saveConsent({ necessary: true, analytics: true, timestamp: 0 });
    };

    const handleAcceptNecessary = () => {
        saveConsent({ necessary: true, analytics: false, timestamp: 0 });
    };

    const handleSaveSettings = () => {
        saveConsent(consent);
    };

    const toggleAnalytics = () => {
        setConsent(prev => ({ ...prev, analytics: !prev.analytics }));
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className={styles.overlay}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <motion.div
                        className={styles.banner}
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                    >
                        <div className={styles.header}>
                            <div className={styles.titleWrapper}>
                                <Cookie size={24} className={styles.icon} />
                                <h3 className={styles.title}>{t.title}</h3>
                            </div>
                            <button
                                className={styles.closeButton}
                                onClick={handleAcceptNecessary}
                                aria-label="Close"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className={styles.content}>
                            <p className={styles.description}>
                                {t.description}{' '}
                                {t.moreInfo}{' '}
                                <Link to="/datenschutz" className={styles.link}>
                                    {t.privacyLink}
                                </Link>.
                            </p>

                            <AnimatePresence>
                                {showSettings && (
                                    <motion.div
                                        className={styles.settings}
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {/* Necessary Cookies */}
                                        <div className={styles.category}>
                                            <div className={styles.categoryHeader}>
                                                <div className={styles.categoryInfo}>
                                                    <h4>{t.categories.necessary.title}</h4>
                                                    <span className={styles.required}>
                                                        {t.categories.necessary.required}
                                                    </span>
                                                </div>
                                                <div className={`${styles.toggle} ${styles.toggleDisabled}`}>
                                                    <Check size={16} />
                                                </div>
                                            </div>
                                            <p className={styles.categoryDescription}>
                                                {t.categories.necessary.description}
                                            </p>
                                        </div>

                                        {/* Analytics Cookies */}
                                        <div className={styles.category}>
                                            <div className={styles.categoryHeader}>
                                                <div className={styles.categoryInfo}>
                                                    <h4>{t.categories.analytics.title}</h4>
                                                </div>
                                                <button
                                                    className={`${styles.toggle} ${consent.analytics ? styles.toggleActive : ''}`}
                                                    onClick={toggleAnalytics}
                                                    aria-pressed={consent.analytics}
                                                >
                                                    {consent.analytics && <Check size={16} />}
                                                </button>
                                            </div>
                                            <p className={styles.categoryDescription}>
                                                {t.categories.analytics.description}
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className={styles.actions}>
                            {!showSettings ? (
                                <>
                                    <button
                                        className={styles.settingsButton}
                                        onClick={() => setShowSettings(true)}
                                    >
                                        <Settings size={16} />
                                        {t.settings}
                                    </button>
                                    <button
                                        className={styles.secondaryButton}
                                        onClick={handleAcceptNecessary}
                                    >
                                        {t.acceptNecessary}
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleAcceptAll}
                                    >
                                        {t.acceptAll}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className={styles.settingsButton}
                                        onClick={() => setShowSettings(false)}
                                    >
                                        ← {language === 'de' ? 'Zurück' : 'Back'}
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleSaveSettings}
                                    >
                                        {t.saveSettings}
                                    </button>
                                </>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CookieConsent;
